/* @flow */
import * as React from 'react';
import { observable, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';

import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import Loading from 'components/Loading/Loading';

import {
  autoCompleteTradingAccounts,
  autoCompleteManufacturers,
  autoCompleteProducts,
  autoCompleteAccountCompanies,
  autoCompleteCompanySites,
  autoCompleteBuildings,
  autoCompleteCountries,
} from 'api/forms.js';

// Action Drawer actions forms
import AddNewCompany from 'views/ActionDrawer/forms/locationFormActions/AddNewCompany/AddNewCompany';
import AddNewSite from 'views/ActionDrawer/forms/locationFormActions/AddNewSite/AddNewSite';
import AddNewBuilding from 'views/ActionDrawer/forms/locationFormActions/AddNewBuilding/AddNewBuilding';
import AddNewRoom from 'views/ActionDrawer/forms/locationFormActions/AddNewRoom/AddNewRoom';
import AddNewSystem from 'views/ActionDrawer/forms/locationFormActions/AddNewSystem/AddNewSystem';

import { makeFields, locationEdit } from './setupLocationForm';
import ElectroSelect from 'forms/components/inputs/ElectroSelect/ElectroSelect';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import FormControls from '../components/controls/FormControls/FormControls';
import ElectroResponseMessage from 'forms/components/inputs/ElectroResponseMessage';
import ElectroCheckbox from 'forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import RequireForAccess from 'react-access';
import { assetRequestAdmins } from '../../permissions/general';

// MUI radio controls
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import FormGroup from '@material-ui/core/FormGroup';

import AccountStore from '../../stores/AccountStore';

type TypeProps = {|
  mode: string,
  itemData: Object,
  accountStore: AccountStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore', 'accountStore', 'userStore')
@observer
export default class LocationForm extends React.Component<*> {
  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteTradingAccounts);
  @observable manufacturerModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteManufacturers
  );
  @observable modelProduct: AutoCompleteModel = new AutoCompleteModel(autoCompleteProducts);
  @observable companyModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteAccountCompanies);
  @observable sitesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCompanySites);
  @observable buildingsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);
  @observable roomsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);
  @observable systemsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);
  @observable countryModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCountries);

  dontUpdate: false;

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  // Build form on demand. It reuses makeFields (which already sets the initial radio value).
  @computed get form(): Object {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const accountStore: TypeProps = this.props.accountStore;

    typeof this.props.itemData === 'undefined' && (itemData.id = this.props.params.id);

    if (!itemData.locationDetails.id) {
      // Company level vs Location level detail source
      if (itemData.titleEnd === 'company' || itemData.titleEnd === 'Company') {
        itemData.locationDetails = accountStore.companyDetails;
      } else {
        itemData.locationDetails = accountStore.locationDetails;
      }
    }

    const fields: Array<*> = makeFields(itemData, this.props.t, this.props.accountStore);

    return (
      this.props.mode === 'edit' &&
      locationEdit(fields, itemData, this.localState, this.props.accountStore, this.props.t)
    );
  }

  // Dynamically set required rules for assetized container children
  assetCalc: Function = (form: Object, field: boolean) => {
    const rec = field ? 'required' : '';
    form.set('rules', {
      locationAssetDetails: {
        assetType: rec,
        assetDescription: rec,
        assetNumber: rec,
        assetBarcodeNumber: rec,
        assetSerialNumber: rec,
      },
    });
  };

  async componentDidMount(): void {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const accountStore: TypeProps = this.props.accountStore;

    // Load details depending on context (company vs location)
    if (itemData.titleEnd === 'company' || itemData.titleEnd === 'Company') {
      if (!accountStore.companyDetails || accountStore.companyDetails.id !== itemData.locationId) {
        await accountStore.loadCompanyDetails(itemData.locationId);
        await accountStore.loadDetails(itemData.accountId);
      }
    } else if (
      !accountStore.locationDetails ||
      accountStore.locationDetails.id !== itemData.locationId
    ) {
      this.dontUpdate = true;
      this.localState.isLoading = true;
      await accountStore.loadLocationDetails(itemData.locationId);
      await accountStore.loadCompanyDetails(itemData.details.company_id);
      await accountStore.loadDetails(itemData.accountId);
      this.localState.isLoading = false;
      this.dontUpdate = false;
    }

    this.setLocationColumn(itemData.titleEnd);
  }

  componentDidUpdate() {
    if (!this.dontUpdate) {
      this.assetCalc(this.form, this.form.$('locationAssetDetails.assetizedLocation').value);
    }
  }

  // Persist which column we are editing in the store (unchanged behavior)
  setLocationColumn(titleEnd) {
    const accountStore: TypeProps = this.props.accountStore;
    const LOCATION_COLUMN_COMPANY = 0;
    const LOCATION_COLUMN_SITE = 1;
    const LOCATION_COLUMN_BUILDING = 2;
    const LOCATION_COLUMN_ROOM = 3;
    const LOCATION_COLUMN_SYSTEM = 4;

    switch (titleEnd) {
      case 'company':
      case 'Company':
        accountStore.locationColumn = LOCATION_COLUMN_COMPANY;
        break;
      case 'site':
      case 'Site':
        accountStore.locationColumn = LOCATION_COLUMN_SITE;
        break;
      case 'building':
      case 'Building/Transport':
        accountStore.locationColumn = LOCATION_COLUMN_BUILDING;
        break;
      case 'room':
      case 'Lab/Room':
        accountStore.locationColumn = LOCATION_COLUMN_ROOM;
        break;
      case 'workspace':
      case 'System/Workspace':
        accountStore.locationColumn = LOCATION_COLUMN_SYSTEM;
        break;
    }
  }

  // Compute visibility of the radio group based on account capabilities
  radioIsVisible = (): boolean => {
    const details = this.props.accountStore?.details?.details || {};
    const assetEnabled = Boolean(details.asset_request_form_enabled);
    const rushEnabled = Boolean(details.is_rush_asset_request_form_customer);
    // Show radio if either asset OR rush are enabled
    return assetEnabled || rushEnabled;
  };

  render(): React.Element<*> {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const accountStore: TypeProps = this.props.accountStore;
    const { t } = this.props;

    if (this.localState.isLoading) {
      return <Loading />;
    }

    if (itemData.titleEnd === 'company' || itemData.titleEnd === 'Company') {
      if (!accountStore.companyDetails || accountStore.companyDetails.id !== itemData.locationId) {
        return <Loading />;
      }
    } else if (
      !accountStore.locationDetails ||
      accountStore.locationDetails.id !== itemData.locationId
    ) {
      return <Loading />;
    }

    const form: Object = this.form;
    const accountsModel: Object = this.accountsModel;
    const companyModel: Object = this.companyModel;
    const sitesModel: Object = this.sitesModel;
    const buildingsModel: Object = this.buildingsModel;
    const roomsModel: Object = this.roomsModel;
    const systemsModel: Object = this.systemsModel;
    const countryModel: Object = this.countryModel;

    // Read current value once for rendering
    const locationTypeField = form.$('locationRequestFormType.list');

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form}>
            {/* ================| LOCATION (HIERARCHY) |================ */}
            <FormSection form={form} section="location">
              <ElectroTextField
                field={form.$('location.locationCompany')}
                disabled={itemData.titleEnd !== 'Company' && itemData.titleEnd !== 'company'}
              />
              <ElectroTextField
                field={form.$('location.locationSite')}
                disabled={itemData.titleEnd !== 'Site' && itemData.titleEnd !== 'site'}
              />
              <ElectroTextField
                field={form.$('location.locationBuilding')}
                disabled={
                  itemData.titleEnd !== 'Building/Transport' && itemData.titleEnd !== 'building'
                }
              />
              <ElectroTextField
                field={form.$('location.locationRoom')}
                disabled={itemData.titleEnd !== 'Lab/Room' && itemData.titleEnd !== 'room'}
              />
              <ElectroTextField
                field={form.$('location.locationSystem')}
                disabled={
                  itemData.titleEnd !== 'System/Workspace' && itemData.titleEnd !== 'workspace'
                }
              />
            </FormSection>

            {/* ================| ADDRESS |================ */}
            <FormSection form={form} section="locationAddressDetails">
              <ElectroTextField
                field={form.$('locationAddressDetails.streetNo')}
                disabled={itemData.titleEnd !== 'Site' && itemData.titleEnd !== 'site'}
              />
              <ElectroTextField
                field={form.$('locationAddressDetails.townCity')}
                disabled={itemData.titleEnd !== 'Site' && itemData.titleEnd !== 'site'}
              />
              <ElectroTextField
                field={form.$('locationAddressDetails.stateCountryProvince')}
                disabled={itemData.titleEnd !== 'Site' && itemData.titleEnd !== 'site'}
              />
              <ElectroTextField
                field={form.$('locationAddressDetails.zipCode')}
                disabled={itemData.titleEnd !== 'Site' && itemData.titleEnd !== 'site'}
              />
              <ElectroSelectExtra
                field={form.$('locationAddressDetails.country')}
                form={form}
                formName="locationAddressDetails"
                labelKey="name"
                props={{ model: countryModel }}
                disabled={itemData.titleEnd !== 'Site' && itemData.titleEnd !== 'site'}
              />
            </FormSection>

            {/* ================| CONTACT |================ */}
            <FormSection form={form} section="locationContactDetails">
              <ElectroTextField
                field={form.$('locationContactDetails.phoneNumber')}
                disabled={itemData.titleEnd === 'Company' || itemData.titleEnd === 'company'}
              />
              <ElectroTextField
                field={form.$('locationContactDetails.faxNumber')}
                disabled={itemData.titleEnd === 'Company' || itemData.titleEnd === 'company'}
              />
              <ElectroTextField
                field={form.$('locationContactDetails.locationContact')}
                disabled={itemData.titleEnd === 'Company' || itemData.titleEnd === 'company'}
              />
            </FormSection>

            {/* ================| ASSETIZED (ONLY WORKSPACE) |================ */}
            {(itemData.titleEnd === 'System/Workspace' || itemData.titleEnd === 'workspace') && (
              <FormSection form={form} section="locationAssetDetails">
                <ElectroCheckbox field={this.form.$('locationAssetDetails.assetizedLocation')} />
                {this.form.$('locationAssetDetails.assetizedLocation').value && (
                  <React.Fragment>
                    <ElectroSelect
                      field={this.form.$('locationAssetDetails.assetType')}
                      simpleValue={true}
                    />
                    <ElectroTextField field={form.$('locationAssetDetails.assetDescription')} />
                    <ElectroTextField field={form.$('locationAssetDetails.assetNumber')} />
                    <ElectroTextField field={form.$('locationAssetDetails.assetBarcodeNumber')} />
                    <ElectroTextField field={form.$('locationAssetDetails.assetSerialNumber')} />
                  </React.Fragment>
                )}
              </FormSection>
            )}

            {/* ================| LOCATION TYPE (ASSET / RUSH) |================
                Show radio when user is at Company level AND account has asset or rush enabled.
                The options are already injected by makeFields and the current value is preserved.
            */}
            {(itemData.titleEnd === 'Company' || itemData.titleEnd === 'company') &&
              this.radioIsVisible() && (
                <RequireForAccess permissions={assetRequestAdmins}>
                  <FormSection form={form} section="locationRequestFormType">
                    <FormControl>
                      {locationTypeField.options.map(opt => (
                        <FormGroup key={opt.id}>
                          <FormControlLabel
                            value={String(opt.value)}
                            label={opt.label}
                            control={<Radio checked={opt.value === locationTypeField.value} />}
                            // IMPORTANT: keep the form state in sync on user selection
                            onChange={(evt: SyntheticInputEvent<*>) =>
                              locationTypeField.set(evt.target.value)
                            }
                          />
                        </FormGroup>
                      ))}
                    </FormControl>
                  </FormSection>
                </RequireForAccess>
              )}

            {/* ================| CONTROLS |================ */}
            <FormControls
              form={form}
              buttonText={
                this.props.mode === 'edit'
                  ? t('general:actions.update')
                  : t('general:actions.create')
              }
            />
          </FormWrapper>
        </PageWrapper>

        {/* ================| ACTION DRAWER |================ */}
        <ActionDrawerWrapper
          onActionCompleted={() => {
            /* overriding with empty fn */
          }}
        >
          {
            {
              newCompany: (
                <AddNewCompany
                  parentForm={form}
                  accountsModel={accountsModel}
                  model={companyModel}
                  childmodel={sitesModel}
                  resetModels={[sitesModel, buildingsModel, roomsModel, systemsModel]}
                  resetFields={[
                    form.$('location.locationSite'),
                    form.$('location.locationBuilding'),
                    form.$('location.locationRoom'),
                    form.$('location.locationSystem'),
                  ]}
                />
              ),
              newSite: (
                <AddNewSite
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={sitesModel}
                  childmodel={buildingsModel}
                  resetModels={[buildingsModel, roomsModel, systemsModel]}
                  resetFields={[
                    form.$('location.locationBuilding'),
                    form.$('location.locationRoom'),
                    form.$('location.locationSystem'),
                  ]}
                />
              ),
              newBuilding: (
                <AddNewBuilding
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={buildingsModel}
                  childmodel={roomsModel}
                  resetModels={[roomsModel, systemsModel]}
                  resetFields={[form.$('location.locationRoom'), form.$('location.locationSystem')]}
                />
              ),
              newRoom: (
                <AddNewRoom
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={roomsModel}
                  childmodel={systemsModel}
                  resetModels={[systemsModel]}
                  resetFields={[form.$('location.locationSystem')]}
                />
              ),
              newSystem: (
                <AddNewSystem
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={systemsModel}
                />
              ),
            }[this.props.actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
